import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportChat = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Olá! Sou o assistente de suporte do Process Body. Como posso ajudar-te hoje?',
      options: [
        'Tenho um problema técnico',
        'Dúvidas sobre planos',
        'Como funciona a app?',
        'Falar com suporte'
      ]
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [planSummary, setPlanSummary] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/plans`)
      .then((res) => {
        const summary = (res.data.plans || [])
          .map((p) => {
            const price = p.price > 0 ? ` (€${Number(p.price).toFixed(2)}/mês)` : '';
            return `• **${p.name}${price}** - ${(p.features || []).slice(0, 2).join(', ')}`;
          })
          .join('\n');
        if (summary) setPlanSummary(summary);
      })
      .catch(() => {});
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const FAQ_RESPONSES = {
    'Tenho um problema técnico': {
      text: 'Lamento que estejas a ter problemas. Podes descrever o que está a acontecer? Algumas soluções comuns:',
      options: [
        'A app não carrega',
        'Não consigo fazer login',
        'Erro ao gerar plano',
        'Outro problema'
      ]
    },
    'A app não carrega': {
      text: 'Tenta as seguintes soluções:\n\n1. Limpa a cache do navegador\n2. Tenta num navegador diferente\n3. Verifica a tua ligação à internet\n4. Desativa extensões do navegador\n\nSe o problema persistir, envia-nos um email para suporte@processbody.com',
      options: ['Resolvido!', 'Ainda não funciona']
    },
    'Não consigo fazer login': {
      text: 'Para problemas de login:\n\n1. Verifica se o email está correto\n2. Tenta recuperar a password\n3. Limpa os cookies do navegador\n4. Tenta o modo incógnito\n\nSe precisares de recuperar a conta, contacta-nos.',
      options: ['Resolvido!', 'Preciso de mais ajuda']
    },
    'Erro ao gerar plano': {
      text: 'Se estás a ter erros ao gerar planos:\n\n1. Verifica se o teu perfil está completo\n2. Tenta atualizar a página\n3. Aguarda alguns segundos e tenta novamente\n\nO sistema de IA pode estar ocupado em horários de pico.',
      options: ['Resolvido!', 'O erro persiste']
    },
    'Dúvidas sobre planos': {
      text: planSummary
        ? `Temos os seguintes planos:\n\n${planSummary}\n\nQual é a tua dúvida?`
        : 'Podes ver todos os planos e preços atualizados na página "Planos". Qual é a tua dúvida?',
      options: ['Como faço upgrade?', 'Posso cancelar?', 'Métodos de pagamento']
    },
    'Como faço upgrade?': {
      text: 'Para fazer upgrade:\n\n1. Faz login na tua conta\n2. Vai a "Planos" no menu\n3. Escolhe o plano desejado\n4. Completa o pagamento via Stripe\n\nO upgrade é imediato após o pagamento!',
      options: ['Obrigado!', 'Mais dúvidas']
    },
    'Posso cancelar?': {
      text: 'Sim, podes cancelar a qualquer momento!\n\nO cancelamento mantém o acesso até ao fim do período pago. Não há taxas de cancelamento.\n\nPara cancelar, vai a Perfil > Gerir Subscrição.',
      options: ['Entendido!', 'Mais dúvidas']
    },
    'Como funciona a app?': {
      text: 'O Process Body AI funciona assim:\n\n1. 📝 Crias o teu perfil com dados físicos e objetivos\n2. 🤖 A IA analisa os teus dados\n3. 📋 Gera planos de treino personalizados\n4. 📈 Acompanhas a tua progressão\n5. 💬 Tiras dúvidas com o assistente\n\nÉ simples e eficaz!',
      options: ['Como começo?', 'Que tipos de treino?', 'Integra com Strava?']
    },
    'Que tipos de treino?': {
      text: 'Oferecemos vários tipos de treino:\n\n• 🏃 Cardio (corrida, ciclismo, natação...)\n• 💪 Musculação (9 grupos musculares)\n• ⚡ HIIT (intervalos de alta intensidade)\n• 🔄 Circuito (combinado)\n\nTodos adaptados ao teu nível!',
      options: ['Quero começar!', 'Mais dúvidas']
    },
    'Falar com suporte': {
      text: 'Para falar com a nossa equipa:\n\n📧 Email: suporte@processbody.com\n⏰ Resposta em até 24h\n\nOu descreve o teu problema aqui e tentamos ajudar!',
      options: ['Enviar mensagem', 'Voltar ao início']
    },
    'Resolvido!': {
      text: 'Ótimo! Fico feliz em ajudar. 😊\n\nHá mais alguma coisa em que possa ajudar?',
      options: ['Sim, outra dúvida', 'Não, obrigado!']
    },
    'Obrigado!': {
      text: 'De nada! Estamos sempre aqui para ajudar. Bons treinos! 💪',
      options: ['Nova pergunta', 'Fechar chat']
    },
    'Entendido!': {
      text: 'Perfeito! Qualquer dúvida, estamos aqui. 😊',
      options: ['Nova pergunta', 'Fechar chat']
    },
    'Não, obrigado!': {
      text: 'Obrigado por usar o Process Body! Bons treinos! 💪🏋️',
      options: ['Nova pergunta', 'Fechar chat']
    },
    'Voltar ao início': {
      text: 'Olá! Como posso ajudar-te?',
      options: [
        'Tenho um problema técnico',
        'Dúvidas sobre planos',
        'Como funciona a app?',
        'Falar com suporte'
      ]
    },
    'Nova pergunta': {
      text: 'Claro! Qual é a tua dúvida?',
      options: [
        'Tenho um problema técnico',
        'Dúvidas sobre planos',
        'Como funciona a app?',
        'Falar com suporte'
      ]
    },
    'Sim, outra dúvida': {
      text: 'Claro! Qual é a tua dúvida?',
      options: [
        'Tenho um problema técnico',
        'Dúvidas sobre planos',
        'Como funciona a app?',
        'Falar com suporte'
      ]
    },
    'Quero começar!': {
      text: 'Excelente decisão! 🎉\n\nPara começar:\n1. Clica em "Começar" no topo da página\n2. Cria a tua conta gratuita\n3. Preenche o teu perfil\n4. Gera o teu primeiro plano!\n\nÉ rápido e fácil!',
      options: ['Obrigado!', 'Mais dúvidas']
    },
    'Como começo?': {
      text: 'Para começar:\n\n1. Clica em "Começar" ou "Criar o Meu Plano"\n2. Regista-te com email e password\n3. Preenche o teu perfil (idade, peso, objetivos)\n4. Vai a "Gerar Plano" e escolhe o tipo de treino\n\nEm segundos tens o teu plano!',
      options: ['Vou experimentar!', 'Mais dúvidas']
    },
    'Vou experimentar!': {
      text: 'Boa! Qualquer dúvida, estamos aqui. Bons treinos! 💪',
      options: ['Nova pergunta', 'Fechar chat']
    }
  };

  const handleOptionClick = (option) => {
    // Add user message
    setMessages(prev => [...prev, { type: 'user', text: option }]);

    if (option === 'Fechar chat') {
      setTimeout(() => setIsOpen(false), 500);
      return;
    }

    // Get bot response
    setIsLoading(true);
    setTimeout(() => {
      const response = FAQ_RESPONSES[option] || {
        text: 'Obrigado pela tua mensagem. Para questões específicas, por favor contacta suporte@processbody.com',
        options: ['Voltar ao início', 'Fechar chat']
      };
      
      setMessages(prev => [...prev, { type: 'bot', ...response }]);
      setIsLoading(false);
    }, 500);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setInputValue('');
    setIsLoading(true);

    // Simple keyword matching for custom messages
    setTimeout(() => {
      let response;
      const lowerMessage = userMessage.toLowerCase();

      if (lowerMessage.includes('erro') || lowerMessage.includes('problema') || lowerMessage.includes('não funciona')) {
        response = FAQ_RESPONSES['Tenho um problema técnico'];
      } else if (lowerMessage.includes('preço') || lowerMessage.includes('plano') || lowerMessage.includes('pagar')) {
        response = FAQ_RESPONSES['Dúvidas sobre planos'];
      } else if (lowerMessage.includes('como') || lowerMessage.includes('funciona')) {
        response = FAQ_RESPONSES['Como funciona a app?'];
      } else if (lowerMessage.includes('obrigado') || lowerMessage.includes('thanks')) {
        response = FAQ_RESPONSES['Obrigado!'];
      } else {
        response = {
          text: `Obrigado pela tua mensagem. Para questões mais específicas, a nossa equipa de suporte pode ajudar melhor:\n\n📧 suporte@processbody.com\n\nOu escolhe uma das opções abaixo:`,
          options: [
            'Tenho um problema técnico',
            'Dúvidas sobre planos',
            'Como funciona a app?',
            'Fechar chat'
          ]
        };
      }

      setMessages(prev => [...prev, { type: 'bot', ...response }]);
      setIsLoading(false);
    }, 800);
  };

  // Hide the floating support widget on the AI Assistant page to avoid duplicate chat UIs
  if (location.pathname === '/assistant') {
    return null;
  }

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-5 right-4 sm:bottom-6 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'bg-red-500 hover:bg-red-600' : 'bg-[#32CD32] hover:bg-[#2ab32a] animate-pulse'
        }`}
        data-testid="support-chat-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-black" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 sm:bottom-24 left-4 right-4 sm:left-auto sm:right-6 z-50 w-auto sm:w-96 bg-[#121212] rounded-2xl shadow-2xl border border-white/10 overflow-hidden animate-fadeInUp">
          {/* Header */}
          <div className="bg-[#32CD32] p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-black/20 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-black" />
            </div>
            <div>
              <h3 className="font-bold text-black">Suporte Process Body</h3>
              <p className="text-xs text-black/70">Normalmente respondemos em minutos</p>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[45vh] sm:h-80 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div key={index}>
                {message.type === 'bot' ? (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#32CD32]/20 flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="w-4 h-4 text-[#32CD32]" />
                    </div>
                    <div className="flex-1">
                      <div className="bg-black/30 rounded-2xl rounded-tl-sm p-3">
                        <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                      </div>
                      {message.options && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {message.options.map((option, optIndex) => (
                            <button
                              key={optIndex}
                              onClick={() => handleOptionClick(option)}
                              className="text-xs px-3 py-1.5 rounded-full bg-[#32CD32]/10 text-[#32CD32] hover:bg-[#32CD32]/20 transition-colors border border-[#32CD32]/20"
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <div className="bg-[#32CD32] text-black rounded-2xl rounded-tr-sm p-3 max-w-[80%]">
                      <p className="text-sm">{message.text}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#32CD32]/20 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-[#32CD32] animate-spin" />
                </div>
                <div className="bg-black/30 rounded-2xl rounded-tl-sm p-3">
                  <p className="text-sm text-gray-400">A escrever...</p>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Escreve a tua mensagem..."
                className="flex-1 bg-black/30 border-white/10 rounded-full px-4"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 rounded-full bg-[#32CD32] hover:bg-[#2ab32a] p-0"
              >
                <Send className="w-4 h-4 text-black" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChat;
